import { router } from "@inertiajs/react"
import { AlertTriangle } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"

/**
 * OKS-552 — Inline error state for the creator Activity feed (`/activity`).
 *
 * Mirrors the OKS-63 `analytics-error-state`: a centered card with an
 * `AlertTriangle`, a title, a muted body line, and an outline Retry
 * button. Rendered when the DEFERRED `activity_feed` prop resolves to
 * `null` (the controller's `rescue → nil`, TRD §3), keeping the page
 * chrome (header + filter chips) painted above it.
 *
 * Retry is a partial reload of ONLY the `activity_feed` prop — it re-runs
 * the same creator-scoped `#index` deferred block in place, with no full
 * navigation and no loss of chrome (UX Spec §2/§4). On success the feed
 * replaces this card; on repeat failure the card returns (idempotent).
 *
 * A11y (UX Spec §6): the card is a `role="alert"` live region so the
 * failure is announced to assistive tech; the icon is `aria-hidden`
 * (the error is conveyed by text + button, not icon/color alone).
 */
export function ActivityErrorState() {
  const { t } = useTranslation()

  const retry = () => {
    router.reload({ only: ["activity_feed"] })
  }

  return (
    <div className="px-4 sm:px-0">
      <Card
        role="alert"
        data-testid="activity-error-state"
        className="mx-auto flex max-w-md flex-col items-center gap-3 p-6 text-center"
      >
        <CardContent className="flex flex-col items-center gap-3 p-0">
          <AlertTriangle
            className="text-destructive h-8 w-8"
            aria-hidden="true"
          />
          <h2 className="text-foreground text-base font-semibold">
            {t("activity.error.title")}
          </h2>
          <p className="text-muted-foreground text-sm">
            {t("activity.error.body")}
          </p>
          <Button
            variant="outline"
            onClick={retry}
            data-testid="activity-error-state-retry"
            className="min-h-11"
          >
            {t("activity.error.retry")}
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
